/**
 * Rewards Redux Slice
 * 
 * Clean state management for reward catalog list with filtering and pagination.
 * Follows rewards-mfe pattern: simple synchronous reducers, hook handles API calls.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import type {
  RewardResponse,
  RewardQueryParams,
} from '@/app/types/rewards';

// ============================================================================
// Types
// ============================================================================

interface RewardsState {
  items: RewardResponse[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filters: RewardQueryParams;
  isLoading: boolean;
  error: string | null;
  invalidated: boolean;
}

// ============================================================================
// Constants
// ============================================================================

// Page size options available to users
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PageSizeOption = typeof PAGE_SIZE_OPTIONS[number];

// Default page size
const DEFAULT_PAGE_SIZE: PageSizeOption = 10;

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: RewardQueryParams = {
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

const initialState: RewardsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: initialFilters,
  isLoading: false,
  error: null,
  invalidated: false,
};

// ============================================================================
// Slice
// ============================================================================

const rewardsSlice = createSlice({
  name: 'rewards',
  initialState,
  reducers: {
    // Loading state management (replaces async thunk pending/fulfilled/rejected)
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    
    setData: (state, action: PayloadAction<{ rewards: RewardResponse[]; total: number }>) => {
      state.items = action.payload.rewards;
      state.totalCount = action.payload.total;
      state.isLoading = false;
      state.error = null;
      state.invalidated = false;
    },
    
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    
    // Filter and pagination management
    setFilters: (state, action: PayloadAction<Partial<RewardQueryParams>>) => {
      // Merge new filters with existing, reset to page 1
      state.filters = {
        ...state.filters,
        ...action.payload,
        offset: 0, // Reset pagination when filters change
      };
      state.currentPage = 1;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      const page = action.payload;
      state.currentPage = page;
      state.filters.offset = (page - 1) * state.pageSize;
    },
    
    setPageSize: (state, action: PayloadAction<PageSizeOption>) => {
      state.pageSize = action.payload;
      state.filters.limit = action.payload;
      state.filters.offset = 0; // Reset to first page when changing page size
      state.currentPage = 1;
    },
    
    clearFilters: (state) => {
      state.filters = { ...initialFilters };
      state.currentPage = 1;
    },
    
    invalidate: (state) => {
      state.invalidated = true;
    },
    
    resetState: () => initialState,
  },
});

// ============================================================================
// Actions & Selectors
// ============================================================================

export const { 
  setLoading, 
  setData, 
  setError, 
  setFilters, 
  setPage, 
  setPageSize, 
  clearFilters, 
  invalidate, 
  resetState 
} = rewardsSlice.actions;

// Selectors
export const selectRewards = (state: RootState) => state.rewards.items;
export const selectRewardsTotalCount = (state: RootState) => state.rewards.totalCount;
export const selectRewardsCurrentPage = (state: RootState) => state.rewards.currentPage;
export const selectRewardsPageSize = (state: RootState) => state.rewards.pageSize;
export const selectRewardsFilters = (state: RootState) => state.rewards.filters;
export const selectRewardsIsLoading = (state: RootState) => state.rewards.isLoading;
export const selectRewardsError = (state: RootState) => state.rewards.error;
export const selectRewardsInvalidated = (state: RootState) => state.rewards.invalidated;

// Derived selectors
export const selectRewardsTotalPages = (state: RootState) => 
  Math.ceil(state.rewards.totalCount / state.rewards.pageSize);

export default rewardsSlice.reducer;
